import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '#services';
import { catchError, from, mergeMap, throwError } from 'rxjs';
import { AuthResponse, ServerHashParamKeys } from 'msal';
import { environment } from '#environments';

// Prevents hanging forever when acquireTokenSilent triggers a loginRedirect
const TOKEN_TIMEOUT_MS = 15_000;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const _authService = inject(AuthService);
  const isIFrame = window !== window.parent && !window.opener;

  // Skip authentication for non-API requests or iframe contexts
  if (!_authService.isAuthenticated() || !req.url.includes(environment.apiUrl) || isIFrame) {
    return next(req);
  }

  const getAuthedReq = () =>
    Promise.race([
      _authService.acquireTokenSilent(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Token acquisition timed out')), TOKEN_TIMEOUT_MS)
      ),
    ]).then((response: AuthResponse) => {
      const accessToken =
        response.tokenType === ServerHashParamKeys.ID_TOKEN ? response.idToken.rawIdToken : response.accessToken;
      return req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });
    });

  return from(getAuthedReq()).pipe(
    mergeMap(authedReq => next(authedReq)),
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        // Retry once with a fresh token before giving up — covers just-expired tokens
        return from(getAuthedReq()).pipe(
          mergeMap(authedReq => next(authedReq)),
          catchError(retryError => {
            if (retryError instanceof HttpErrorResponse && (retryError.status === 401 || retryError.status === 403)) {
              _authService.logout();
            }
            return throwError(() => retryError);
          })
        );
      }
      if (error instanceof HttpErrorResponse && error.status === 403) {
        console.error('Access denied, logging out', error);
        _authService.logout();
      } else if (error) {
        console.error('Request error:', error);
      }
      return throwError(() => error);
    })
  );
};
